/**
 * Workflow Service - API functions for step transitions
 *
 * Provides methods for:
 * - Approving steps (advance to next)
 * - Skipping steps (with warning)
 * - Revisiting previous steps
 * - Getting project status
 */

import { jsonAuthHeaders, AUTH_FETCH_INIT } from '../auth/apiAuth';
import Logger from '../../utils/logger';

// Use VITE_API_BASE_URL directly - it already includes /api/v1
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const logger = Logger.create('WorkflowService');

// Response types
export interface ApproveStepResponse {
  status: 'approved' | 'workflow_complete';
  previous_step: string;
  next_step: string | null;
  workflow_complete: boolean;
  message: string;
}

export interface SkipStepResponse {
  status: 'skipped';
  warning: string;
  previous_step: string;
  next_step: string | null;
  workflow_complete: boolean;
  message: string;
}

export interface RevisitStepResponse {
  status: 'revisiting';
  previous_step: string;
  current_step: string;
  data_cleared: boolean;
  cleared_steps: string[];
  message: string;
}

export interface StepStatus {
  status: 'pending' | 'in_progress' | 'approved' | 'skipped';
  has_chat_history: boolean;
  has_spec: boolean;
}

export interface ProjectStatusResponse {
  project_id: string;
  current_step: string;
  learning_mode: boolean;
  steps: Record<string, StepStatus>;
}

// Request types
export interface SkipStepRequest {
  reason?: string;
}

export interface RevisitStepRequest {
  target_step: string;
  clear_subsequent?: boolean;
}

// Chat types
export interface ChatRequest {
  content: string;
  learning_mode?: boolean;
  file_ids?: string[]; // IDs of uploaded files to include in the message
}

export interface SectionFeedback {
  section: string;
  score: number;
  suggestion: string;
  is_weak: boolean;
}

export interface QualityReport {
  score: number;
  score_label?: string; // Display label (e.g., "Design Readiness" for Discovery)
  dimensions: {
    completeness: number;
    specificity: number;
    consistency: number;
    actionability: number;
    expertise: number;
  };
  issues: string[];
  blocking_issues: string[];
  can_proceed: boolean;
  requires_override: boolean;
  section_feedback?: SectionFeedback[];
  coaching_mode?: boolean;
}

export interface ProblemStatementDraft {
  title: string;
  problem: string;
  target_users: string;
  success_criteria: string;
  current_state: string;
  integrations: string[];
  constraints: string[];
}

export interface DraftUpdateResponse {
  status: string;
  draft: ProblemStatementDraft;
  quality_score: number;
  quality_report: QualityReport;
}

export interface CostInfo {
  session_total_usd: number;
  session_input_tokens: number;
  session_output_tokens: number;
  session_request_count: number;
  last_request_cost_usd: number;
  last_request_input_tokens: number;
  last_request_output_tokens: number;
}

// Design specification type from backend
export interface DesignSpec {
  agent_name: string;
  persona: string;
  primary_goal: string;
  secondary_goals: string[];
  capabilities: string[];
  boundaries: string[];
  tone: string;
  autonomy_level: string;
  error_handling: string;

  // Integrations (carried from discovery)
  integrations?: string[];

  // Operational fields (from Design 4 topics)
  input_sources?: {
    user_inputs: string[];
    system_events: string[];
    external_triggers: string[];
  };
  output_types?: string[];
  handoff_rules?: {
    triggers: string[];
    targets: string[];
    payload_fields: string[];
  };
  failure_modes?: {
    missing_data: string;
    tool_failure: string;
    unclear_intent: string;
    rate_limited: string;
  };

  // Business context
  business_assumptions?: {
    expected_users: number;
    usage_per_user: number;
    growth_rate: string;
    value_per_task: string;
  };

  // Capability dependency graph for architecture decisions
  capability_dependencies?: Record<string, string[]>;

  // Response time SLA per capability (in seconds)
  capability_slas?: Record<string, number>;
}

// Architecture output type from Multi-Agent step
export interface AgentDefinition {
  name: string;
  role: string;
  responsibilities: string[];
  tools: string[];
}

export interface CommunicationFlow {
  from: string;
  to: string;
  trigger: string;
}

export interface DiagramNode {
  id: string;
  label: string;
  type: string;
}

export interface DiagramEdge {
  source: string;
  target: string;
  label?: string;
}

export interface DiagramData {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
}

export interface ArchitectureOutput {
  is_multi_agent: boolean;
  architecture_type: 'single' | 'multi_agent';
  agents: AgentDefinition[];
  orchestration_pattern: 'sequential' | 'parallel' | 'hierarchical';
  root_agent: string;
  communication_flows: CommunicationFlow[];
  diagram_data: DiagramData;
  human_checkpoints: string[];
}

// Technical output type from Technical step
export interface ToolDefinition {
  name: string;
  type: string;
  description: string;
  suggested_params: string[];
  async: boolean;
}

export interface SessionConfig {
  service: string;
  db_url: string | null;
  ttl_hours: number;
  persistence_enabled?: boolean;
}

export interface TokenEstimates {
  daily_estimates?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
  monthly_estimates?: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
  optimization_tips?: string[];
  cost_note?: string;
}

export interface TechnicalOutput {
  model: string;
  model_rationale: string;
  tools: ToolDefinition[];
  memory_strategy: string;
  session_config: SessionConfig;
  token_estimates: TokenEstimates;
  integration_patterns: string[];
}

// Code generation output type from CodeGen step
export interface CodegenOutput {
  project_name: string;
  files: Record<string, string>;
  is_multi_agent: boolean;
  agents_count?: number;
  orchestration_pattern?: string;
  file_count?: number;
}

// Operations output types from Operations step
export interface CostEstimateData {
  input_tokens: number;
  output_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  total_cost_usd: number;
}

export interface MonitoringMetric {
  name: string;
  description: string;
  unit: string;
  alert_threshold: number | null;
}

export interface MonitoringConfig {
  agents: string[];
  metrics: MonitoringMetric[];
  dashboards: string[];
}

export interface AlertingConfig {
  email: string;
  conditions: string[];
}

export interface ChecklistItem {
  item: string;
  required: boolean;
}

export interface DeploymentChecklist {
  pre_deployment: ChecklistItem[];
  infrastructure: ChecklistItem[];
  deployment: ChecklistItem[];
  post_deployment: ChecklistItem[];
}

export interface OperationsOutput {
  deployment_platform: string;
  document: string;
  structured_data: {
    cost_estimate: {
      monthly_estimate: CostEstimateData;
      model: string;
      disclaimer: string;
      optimization_tips: string[];
    };
    monitoring_config: {
      monitoring: MonitoringConfig;
      alerting: AlertingConfig;
    };
    deployment_checklist: {
      checklist: DeploymentChecklist;
      deployment_target: string;
    };
  };
}

// Union type for structured data from different steps
export type StepStructuredData =
  | ProblemStatementDraft
  | DesignSpec
  | ArchitectureOutput
  | TechnicalOutput
  | CodegenOutput
  | OperationsOutput
  | Record<string, unknown>;

export interface ChatResponse {
  message: string;
  current_step: string;
  step_complete: boolean;
  spec_ready: boolean;
  detected_complexity?: string;
  explanations: Array<{ title: string; content: string }>;
  metadata: Record<string, unknown>;
  quality_score?: number;
  quality_report?: QualityReport;
  quality_gate_status?: 'passed' | 'soft_blocked' | 'hard_blocked';
  cost_info?: CostInfo;
  // Step output data - type varies by step (discovery, design, etc.)
  structured_data?: StepStructuredData;
}

class WorkflowService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    // task_111: provider-aware. Firebase attaches a Bearer token; Studio omits
    // it (the gml_sso cookie carries auth, sent via credentials: 'include').
    return jsonAuthHeaders();
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...AUTH_FETCH_INIT,
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: response.statusText };
      }
      const errorMessage =
        errorData?.detail ||
        errorData?.error?.message ||
        errorData?.message ||
        'Unknown error';
      logger.error(`API error: ${response.status} - ${errorMessage}`);
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Send a chat message to the ADK agent for the current step.
   *
   * @param projectId - The project ID
   * @param content - The message content
   * @param learningMode - Whether to include educational explanations
   * @param fileIds - Optional array of uploaded file IDs to include with the message
   * @returns ChatResponse with agent's reply and metadata
   */
  async chat(
    projectId: string,
    content: string,
    learningMode: boolean = false,
    fileIds?: string[]
  ): Promise<ChatResponse> {
    logger.info(
      `Sending chat message for project: ${projectId}${fileIds ? ` with ${fileIds.length} file(s)` : ''}`
    );

    const requestBody: ChatRequest = {
      content,
      learning_mode: learningMode,
    };

    if (fileIds && fileIds.length > 0) {
      requestBody.file_ids = fileIds;
    }

    return this.fetchWithAuth<ChatResponse>(`/projects/${projectId}/chat`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }

  /**
   * Approve the current step and advance to the next one.
   *
   * @param projectId - The project ID
   * @returns ApproveStepResponse with new step info
   */
  async approveStep(projectId: string): Promise<ApproveStepResponse> {
    logger.info(`Approving step for project: ${projectId}`);
    return this.fetchWithAuth<ApproveStepResponse>(
      `/projects/${projectId}/approve-step`,
      { method: 'POST' }
    );
  }

  /**
   * Skip the current step and advance to the next one.
   * Shows a warning that later steps may have incomplete context.
   *
   * @param projectId - The project ID
   * @param reason - Optional reason for skipping
   * @returns SkipStepResponse with warning and new step info
   */
  async skipStep(
    projectId: string,
    reason?: string
  ): Promise<SkipStepResponse> {
    logger.info(`Skipping step for project: ${projectId}`, { reason });
    return this.fetchWithAuth<SkipStepResponse>(
      `/projects/${projectId}/skip-step`,
      {
        method: 'POST',
        body: JSON.stringify({ reason: reason || '' }),
      }
    );
  }

  /**
   * Revisit a previous step in the workflow.
   * Optionally clears data from steps after the target step.
   *
   * @param projectId - The project ID
   * @param targetStep - The step to navigate to
   * @param clearSubsequent - Whether to clear data from later steps
   * @returns RevisitStepResponse with navigation info
   */
  async revisitStep(
    projectId: string,
    targetStep: string,
    clearSubsequent: boolean = false
  ): Promise<RevisitStepResponse> {
    logger.info(`Revisiting step: ${targetStep} for project: ${projectId}`, {
      clearSubsequent,
    });
    return this.fetchWithAuth<RevisitStepResponse>(
      `/projects/${projectId}/revisit-step`,
      {
        method: 'POST',
        body: JSON.stringify({
          target_step: targetStep,
          clear_subsequent: clearSubsequent,
        }),
      }
    );
  }

  /**
   * Get the current status of a project including all step statuses.
   *
   * @param projectId - The project ID
   * @returns ProjectStatusResponse with step statuses
   */
  async getProjectStatus(projectId: string): Promise<ProjectStatusResponse> {
    logger.debug(`Getting status for project: ${projectId}`);
    return this.fetchWithAuth<ProjectStatusResponse>(
      `/projects/${projectId}/status`
    );
  }

  /**
   * Update the discovery draft directly (without going through chat).
   * Re-scores quality and returns updated feedback.
   *
   * @param projectId - The project ID
   * @param draft - The updated problem statement draft
   * @returns DraftUpdateResponse with quality feedback
   */
  async updateDiscoveryDraft(
    projectId: string,
    draft: ProblemStatementDraft
  ): Promise<DraftUpdateResponse> {
    logger.info(`Updating discovery draft for project: ${projectId}`);
    return this.fetchWithAuth<DraftUpdateResponse>(
      `/projects/${projectId}/discovery/draft`,
      {
        method: 'PUT',
        body: JSON.stringify(draft),
      }
    );
  }

  /**
   * Get the current discovery draft for a project.
   *
   * @param projectId - The project ID
   * @returns The current draft or null if not yet generated
   */
  async getDiscoveryDraft(
    projectId: string
  ): Promise<ProblemStatementDraft | null> {
    logger.debug(`Getting discovery draft for project: ${projectId}`);
    try {
      const response = await this.fetchWithAuth<{
        draft: ProblemStatementDraft | null;
      }>(`/projects/${projectId}/discovery/draft`);
      return response.draft;
    } catch (error) {
      // Return null if draft doesn't exist yet
      logger.debug(`No draft found for project: ${projectId}`);
      return null;
    }
  }

  /**
   * Get the full project data including all step data.
   * Used for resuming a project from where the user left off.
   *
   * @param projectId - The project ID
   * @returns Full project data including step histories and spec data
   */
  async getFullProject(projectId: string): Promise<FullProjectResponse> {
    logger.info(`Getting full project data for: ${projectId}`);
    return this.fetchWithAuth<FullProjectResponse>(`/projects/${projectId}`);
  }

  /**
   * Save step draft data without going through chat.
   * Allows users to save their progress at any time.
   *
   * @param projectId - The project ID
   * @param stepName - The step name (discovery, design, etc.)
   * @param draftData - The step-specific draft data to save
   * @returns Success status
   */
  async saveStepDraft(
    projectId: string,
    stepName: string,
    draftData: StepStructuredData
  ): Promise<SaveStepDraftResponse> {
    logger.info(
      `Saving step draft for project: ${projectId}, step: ${stepName}`
    );
    return this.fetchWithAuth<SaveStepDraftResponse>(
      `/projects/${projectId}/steps/${stepName}/draft`,
      {
        method: 'PUT',
        body: JSON.stringify({ draft_data: draftData }),
      }
    );
  }

  /**
   * Download the cumulative master document containing all completed step outputs.
   * The document grows as each step completes.
   *
   * @param projectId - The project ID
   * @param format - Output format: 'pdf' or 'markdown'
   * @returns A Blob containing the document data
   */
  async downloadMasterDocument(
    projectId: string,
    format: 'pdf' | 'markdown' = 'pdf'
  ): Promise<Blob> {
    logger.info(
      `Downloading master document for project: ${projectId} (format: ${format})`
    );
    const headers = await this.getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/master-document?format=${format}`,
      { ...AUTH_FETCH_INIT, headers }
    );
    if (!response.ok) {
      let errorMessage = 'Download failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData?.detail || errorMessage;
      } catch {
        // ignore parse errors
      }
      throw new Error(errorMessage);
    }
    return response.blob();
  }
}

// Full project response type
export interface FullProjectResponse {
  id: string;
  user_id: string;
  title: string;
  description: string;
  current_step: string;
  learning_mode: boolean;
  created_at: string;
  updated_at: string;
  step1_discovery: StepDataResponse;
  step2_design: StepDataResponse;
  step3_multiagent: StepDataResponse;
  step4_technical: StepDataResponse;
  step5_codegen: StepDataResponse;
  step6_operations: StepDataResponse;
}

export interface StepDataResponse {
  status: string;
  chat_history: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    file_ids?: string[];
  }>;
  spec_data?: StepStructuredData;
  quality_score?: number;
  quality_report?: QualityReport;
  started_at?: string;
  completed_at?: string;
}

export interface SaveStepDraftResponse {
  status: string;
  message: string;
  step: string;
  updated_at: string;
}

export const workflowService = new WorkflowService();
